import React, {
    forwardRef,
    ForwardRefExoticComponent,
    RefAttributes,
} from 'react';
import { getClonedChild } from '@landr/maestro';
import { useCreatorContext } from 'controllers/business/Creator/Creator.controller';
import * as Styled from './Section.styled';
import BackgroundImage from './BackgroundImage/BackgroundImage';
import BackgroundVideo from './BackgroundVideo/BackgroundVideo';
import BackgroundOverlay from './BackgroundOverlay/BackgroundOverlay';
import Content from './Content/Content';
import { SectionProps } from './Section.types';

Content.displayName = 'Section.Content';

interface SectionSubComponents {
    BackgroundImage: typeof BackgroundImage;
    BackgroundVideo: typeof BackgroundVideo;
    BackgroundOverlay: typeof BackgroundOverlay;
    Content: typeof Content;
}

type SectionComponent = ForwardRefExoticComponent<
    SectionProps & RefAttributes<HTMLElement>
> &
    SectionSubComponents;

export const Section = forwardRef(
    (
        {
            isFullHeight = false,
            minHeight = 'base',
            isHeader = false,
            variant = 'primary',
            position = 'center',
            boxed = 'xl',
            children,
        },
        ref,
    ) => {
        const { isVisible: isCreatorVisible } = useCreatorContext();

        const childrenArray = React.Children.toArray(children);

        const backgroundImage = getClonedChild(
            childrenArray,
            'Section.BackgroundImage',
            {
                key: 'backgroundImage',
            },
        );
        const backgroundVideo = getClonedChild(
            childrenArray,
            'Section.BackgroundVideo',
            {
                key: 'backgroundVideo',
            },
        );
        const backgroundOverlay = getClonedChild(
            childrenArray,
            'Section.BackgroundOverlay',
            {
                key: 'backgroundOverlay',
            },
        );
        const content = getClonedChild(childrenArray, 'Section.Content', boxed);

        const fullHeight = isCreatorVisible
            ? 'fullHeightMinusCreator'
            : 'fullHeight';

        return (
            <Styled.Section
                ref={ref}
                minHeight={isFullHeight ? fullHeight : minHeight}
                offset={isHeader ? 'header' : 'base'}
                position={position}
                variant={variant}
            >
                <Styled.BackgroundWrapper>
                    {backgroundImage}
                    {backgroundVideo}
                    {backgroundOverlay}
                </Styled.BackgroundWrapper>
                <Styled.InnerWrapper boxed={boxed}>
                    {content}
                </Styled.InnerWrapper>
            </Styled.Section>
        );
    },
) as SectionComponent;

Section.BackgroundImage = BackgroundImage;
Section.BackgroundVideo = BackgroundVideo;
Section.BackgroundOverlay = BackgroundOverlay;
Section.Content = Content;
